import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import ReactFlow, {
  Background, Controls, MiniMap,
  useNodesState, useEdgesState, MarkerType,
  ReactFlowProvider, useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";

import {
  fetchChildren, fetchAncestors, fetchMlmRoots,
  buildHierarchy, computeDepths,
  fetchCurrentUserInfo, fetchProfile, isAdminUser,
  countUserNetwork, fetchUserNetworkPage,
  countDescendantsByRoot, fetchDescendantsByRoot,
  countMlmMembers, fetchMlmPage,
} from "../script/rpc";
import MlmNode from "./MlmNode";
import InfoDrawer from "./InfoDrawer";

const nodeTypes = { mlmNode: MlmNode };
const PAGE_SIZE = 50;

const EDGE_COLORS = [
  "#111827", "#7c3aed", "#0ea5e9", "#059669", "#d97706", "#db2777", "#0891b2", "#65a30d",
];

const NODE_W = 220;
const NODE_H = 200;

function computeWidth(node) {
  if (!node.children || node.children.length === 0) { node._w = 1; return; }
  node.children.forEach(computeWidth);
  node._w = node.children.reduce((s, c) => s + c._w, 0);
}

function placeNodes(node, depth, left, rfNodes, rfEdges) {
  const cx = (left + node._w / 2) * NODE_W;
  const y = depth * NODE_H;

  rfNodes.push({
    id: String(node.id),
    type: "mlmNode",
    position: { x: cx - 100, y },
    data: { member: node, depth, isSelected: false, expanded: false, loadingChildren: false, onSelect: () => { }, onExpand: () => { } },
  });

  if (node.parent_id && node.parent_id[0]) {
    const col = EDGE_COLORS[Math.max(0, depth - 1) % EDGE_COLORS.length];
    rfEdges.push({
      id: `e-${node.parent_id[0]}-${node.id}`,
      source: String(node.parent_id[0]),
      target: String(node.id),
      type: "smoothstep",
      style: { stroke: col, strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: col, width: 14, height: 14 },
    });
  }

  let childLeft = left;
  (node.children || []).forEach(child => {
    placeNodes(child, depth + 1, childLeft, rfNodes, rfEdges);
    childLeft += child._w;
  });
}

function buildLayout(roots) {
  const rfNodes = [], rfEdges = [];
  roots.forEach(computeWidth);
  let left = 0;
  roots.forEach(root => {
    placeNodes(root, 0, left, rfNodes, rfEdges);
    left += root._w;
  });
  return { rfNodes, rfEdges };
}

const FATAL_AUTH_ERRORS = [
  "could not fetch user information",
  "could not load user partner account",
  "connection failed",
  "network error",
  "invalid username",
  "access denied",
];

function isFatalAuthError(msg = "") {
  const lower = msg.toLowerCase();
  return FATAL_AUTH_ERRORS.some(pattern => lower.includes(pattern));
}

function MlmTree() {
  const { auth, logout } = useAuth();
  const { uid, password } = auth;
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const { getViewport, setViewport, fitView } = useReactFlow();
  const handleWheel = useCallback((e) => {
    const { x, y, zoom } = getViewport();
    const delta = -e.deltaY * 0.0001;
    const newZoom = Math.min(2, Math.max(0.01, zoom + delta));
    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (!rect) { setViewport({ x, y, zoom: newZoom }); return; }
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const scale = newZoom / zoom;
    const newX = mouseX - scale * (mouseX - x);
    const newY = mouseY - scale * (mouseY - y);
    setViewport({ x: newX, y: newY, zoom: newZoom });
  }, [getViewport, setViewport]);

  const allRecords = useRef({});
  const expandedIds = useRef(new Set());
  const loadingIds = useRef(new Set());
  const rootTreepath = useRef(null);
  const rootPartnerId = useRef(null); // ← tracks the login user's own partnerId
  const isAdmin = useRef(false);       // ← tracks admin status
  const pageOffset = useRef(0);

  const [totalCount, setTotalCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [loadedDescendants, setLoadedDescendants] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [, forceRender] = useState(0);

  const isFitDone = useRef(false);
  const initialFitDone = useRef(false);

  const rebuildTree = useCallback((cbSelect, cbExpand, shouldFit = false) => {
    const records = Object.values(allRecords.current);

    // Filter out stray parentless nodes that are not the designated tree root.
    // rootPartnerId.current is always the actual root node id we want to show.
    const rootId = rootPartnerId.current;
    const filtered = records.filter(r => {
      const hasParent = r.parent_id && r.parent_id[0];
      const isDesignatedRoot = r.id === rootId;
      return hasParent || isDesignatedRoot;
    });

    const roots = computeDepths(buildHierarchy(filtered));
    const { rfNodes, rfEdges } = buildLayout(roots);

    const withData = rfNodes.map(n => ({
      ...n,
      data: {
        ...n.data,
        isSelected: selectedMember && String(selectedMember.id) === n.id,
        expanded: expandedIds.current.has(Number(n.id)),
        loadingChildren: loadingIds.current.has(Number(n.id)),
        onSelect: cbSelect,
        onExpand: cbExpand,
      },
    }));

    setNodes(withData);
    setEdges(rfEdges);

    if (shouldFit || !isFitDone.current) {
      isFitDone.current = true;
      setTimeout(() => {
        fitView({ padding: 0.18, duration: 600 });
      }, 600);
    }
  }, [selectedMember, setNodes, setEdges, fitView]);

  // One-shot fitView after first nodes actually paint to the DOM
  useEffect(() => {
    if (initialFitDone.current) return;
    if (nodes.length === 0) return;
    initialFitDone.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fitView({ padding: 0.18, duration: 600 });
      });
    });
  }, [nodes.length, fitView]);

  const handleSelectRef = useRef(null);
  const handleExpandRef = useRef(null);
  const rebuildRef = useRef(null);
  rebuildRef.current = rebuildTree;

  handleSelectRef.current = (member) => setSelectedMember(member);
  handleExpandRef.current = async (member) => {
    const id = member.id;
    if (expandedIds.current.has(id)) {
      const collect = (parentId) => {
        Object.values(allRecords.current).forEach(r => {
          if (r.parent_id && r.parent_id[0] === parentId) {
            expandedIds.current.delete(r.id);
            collect(r.id);
            delete allRecords.current[r.id];
          }
        });
      };
      collect(id);
      expandedIds.current.delete(id);
      setLoadedCount(Object.keys(allRecords.current).length);
      rebuildRef.current(m => handleSelectRef.current(m), m => handleExpandRef.current(m), false);
      return;
    }
    loadingIds.current.add(id);
    forceRender(c => c + 1);
    rebuildRef.current(m => handleSelectRef.current(m), m => handleExpandRef.current(m), false);

    try {
      const children = await fetchChildren(uid, password, id);

      // For non-admin: filter children to only include those within the
      // logged-in user's subtree (safety guard against fetching up the tree).
      const safeChildren = isAdmin.current
        ? children
        : children.filter(c => {
            const pid = rootPartnerId.current;
            if (!pid) return true;
            // Keep child if its treepath contains the root partner id
            return (
              c.mlm_treepath &&
              (c.mlm_treepath.includes(`/${pid}/`) || c.mlm_treepath.endsWith(`/${pid}`))
            ) || (c.parent_id && c.parent_id[0] === pid) || c.id === pid;
          });

      safeChildren.forEach(c => { allRecords.current[c.id] = c; });
      expandedIds.current.add(id);
    } catch (e) {
      console.error("Failed to fetch children:", e);
    } finally {
      loadingIds.current.delete(id);
    }
    setLoadedCount(Object.keys(allRecords.current).length);
    rebuildRef.current(m => handleSelectRef.current(m), m => handleExpandRef.current(m), false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError("");
      try {
        // ── 1. Get logged-in user info ──────────────────────────────────────
        const userInfo = await fetchCurrentUserInfo(uid, password);
        if (!userInfo || !userInfo.partner_id) throw new Error("Could not fetch user information");
        const partnerId = userInfo.partner_id[0];

        // ── 2. Check admin status ───────────────────────────────────────────
        const [profile, adminCheck] = await Promise.all([
          fetchProfile(uid, password, partnerId),
          isAdminUser(uid, password),
        ]);

        isAdmin.current = adminCheck;
        rootPartnerId.current = partnerId;

        // ── 3. Redirect non-member users without a parent ───────────────────
        if (!adminCheck && profile && !profile.parent_id) {
          navigate("/profile", { replace: true });
          return;
        }

        if (adminCheck) {
          // ── ADMIN PATH: show full global tree ─────────────────────────────
          const ancestors = (profile.parent_id && profile.parent_id[0])
            ? await fetchAncestors(uid, password, [{ ...profile, id: partnerId }])
            : [];
          if (!alive) return;

          const globalRoots = await fetchMlmRoots(uid, password);
          const allKnown = [profile, ...ancestors, ...globalRoots];
          
          // Find the single root of the MLM tree (no parent_id)
          const treeRoot = globalRoots.find(r => !r.parent_id || !r.parent_id[0]) || globalRoots[0];
          rootPartnerId.current = treeRoot ? treeRoot.id : partnerId;
          rootTreepath.current = null; // Admin ignores treepath filter

          const [total, firstPage] = await Promise.all([
            countMlmMembers(uid, password),
            fetchMlmPage(uid, password, { offset: 0, limit: PAGE_SIZE }),
          ]);
          if (!alive) return;

          setTotalCount(total);
          // Seed allRecords with known context (profile, ancestors, global roots)
          allKnown.forEach(r => { allRecords.current[r.id] = r; });
          // Add the first page of global results
          firstPage.forEach(r => {
            allRecords.current[r.id] = r;
            if (r.parent_id && r.parent_id[0]) expandedIds.current.add(r.parent_id[0]);
          });

          // Expand the top-level Administrator node if it exists
          expandedIds.current.add(partnerId);
          // Also expand any global roots found
          globalRoots.forEach(r => expandedIds.current.add(r.id));

          pageOffset.current = firstPage.length;
          setLoadedDescendants(firstPage.length);

        } else {
          // ── NON-ADMIN PATH: show ONLY the logged-in user's own subtree ─────
          //
          // We treat `partnerId` as the root. We load:
          //   • the user's own profile record (as the root node)
          //   • all descendants whose mlm_treepath contains /{partnerId}/
          //
          rootTreepath.current = profile.mlm_treepath || null;

          // Count and fetch first page of descendants (not the user itself)
          const [descendantTotal, firstPage] = await Promise.all([
            countDescendantsByRoot(uid, password, partnerId),
            // fetchDescendantsByRoot returns up to 1000 by default; for paging
            // we replicate the pattern used in the admin path via a direct RPC
            // using the treepath-like filter already in rpc.js.
            fetchDescendantsByRoot(uid, password, partnerId),
          ]);
          if (!alive) return;

          // Total = user themselves + all descendants
          setTotalCount(descendantTotal + 1);

          // Seed with the logged-in user as root node
          allRecords.current[profile.id] = profile;

          // Add all descendant records
          firstPage.forEach(r => {
            allRecords.current[r.id] = r;
            if (r.parent_id && r.parent_id[0]) expandedIds.current.add(r.parent_id[0]);
          });

          // Always expand the root (the logged-in user)
          expandedIds.current.add(partnerId);

          pageOffset.current = firstPage.length;
          setLoadedDescendants(firstPage.length);
        }

        setCurrentUserName(userInfo.name || "User");
        setLoadedCount(Object.keys(allRecords.current).length);
      } catch (e) {
        if (alive) {
          setError(e.message);
          if (isFatalAuthError(e.message))
            setTimeout(() => { logout(); navigate("/login", { replace: true }); }, 2000);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [uid, password, navigate]);

  useEffect(() => {
    if (loading) return;
    rebuildRef.current(m => handleSelectRef.current(m), m => handleExpandRef.current(m), !isFitDone.current);
  }, [loadedCount, loadedDescendants, selectedMember, loading]);

  // ── Load-more handler ───────────────────────────────────────────────────────
  // Admin path uses treepath-based paging. Non-admin already loads all
  // descendants in one shot via fetchDescendantsByRoot (limit 1000). If you
  // have very large non-admin trees you can extend this similarly.
  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const nextPage = isAdmin.current
        ? await fetchMlmPage(uid, password, { offset: pageOffset.current, limit: PAGE_SIZE })
        : await fetchUserNetworkPage(uid, password, rootTreepath.current, { offset: pageOffset.current, limit: PAGE_SIZE });

      nextPage.forEach(r => {
        allRecords.current[r.id] = r;
        if (r.parent_id && r.parent_id[0]) expandedIds.current.add(r.parent_id[0]);
      });
      pageOffset.current += nextPage.length;
      setLoadedDescendants(d => d + nextPage.length);
      setLoadedCount(Object.keys(allRecords.current).length);
    } catch (e) {
      console.error("Load more failed:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [uid, password]);

  return (
    <div
      onContextMenu={(e) => e.preventDefault()}
      style={{ display: "flex", flexDirection: "column", flex: 1, height: "100%", minHeight: 0, background: "#f8fafc" }}
    >
      {loading ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", borderWidth: "4px", borderStyle: "solid", borderColor: "#e2e8f0", borderTopColor: "#7c3aed", animation: "dp-spin 0.8s linear infinite", marginBottom: 16 }} />
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#64748b" }}>Loading network tree…</p>
        </div>
      ) : (
        <div style={{ padding: "14px 24px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", margin: 0 }}>Quantobuzz Tree</h1>
            {!error && (
              <p style={{ fontSize: 12, color: "#64748b", margin: "2px 0 0" }}>
                Viewing <span style={{ fontWeight: 700, color: "#0f172a" }}>{currentUserName}</span>'s network
                {totalCount > 0 && (
                  <span style={{ color: "#94a3b8" }}>
                    {" "}· {loadedDescendants} / {totalCount} loaded
                  </span>
                )}
              </p>
            )}
          </div>
          {/* Load-more only applies to admin path with large trees */}
          {!error && isAdmin.current && totalCount > 50 && loadedDescendants < totalCount && (
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, background: "#e0e7ff", color: "#4338ca", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
            >
              {loadingMore ? "Loading…" : "Load More"}
            </button>
          )}
        </div>
      )}
      {!loading && (
        <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
          {!error && (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              minZoom={0.01}
              maxZoom={2}
              translateExtent={[[-50000, -50000], [50000, 50000]]}
              nodesDraggable
              nodesConnectable={false}
              panOnScroll={false}
              zoomOnScroll={false}
              zoomOnPinch={true}
              onWheel={handleWheel}
              attributionPosition="bottom-right"
            >
              <Background color="#cbd5e1" gap={20} size={1} style={{ background: "#f1f5f9" }} />
              <Controls showInteractive={false} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 }} />
              <MiniMap nodeColor={n => (n.data?.depth === 0) ? "#111827" : EDGE_COLORS[((n.data?.depth ?? 0) - 1) % (EDGE_COLORS.length - 1) + 1]} pannable zoomable />
            </ReactFlow>
          )}
        </div>
      )}
      <InfoDrawer member={selectedMember} onClose={() => setSelectedMember(null)} />
    </div>
  );
}

export default function MlmTreeWrapper() {
  return (
    <ReactFlowProvider>
      <MlmTree />
    </ReactFlowProvider>
  );
}